<div class="utilization-report">
  <section class="section-card today-card">
    <h2 class="card-title">Today</h2>

    @if (isLoadingCurrent) {
      <app-loading-spinner size="large" [isOverlay]="true"></app-loading-spinner>
    } @else if (currentFailed) {
      <app-empty-state
        icon="error_outline"
        title="Failed to load today's utilization"
        subtitle="Check that a TRON network is configured, then try again."
      ></app-empty-state>
    } @else if (current) {
      <div class="indicators-row">
        <div class="indicator-card" data-testid="energy-indicator">
          <div class="indicator-header">
            <span class="indicator-title">Energy utilization today</span>
            <span class="status-pill" [ngClass]="statusClass(current.energyStatus)">{{
              statusLabel(current.energyStatus)
            }}</span>
          </div>
          <div class="indicator-percentage">{{ formatPercent(current.energyUtilizationPercentage) }}</div>
          <div class="indicator-figures">
            {{ formatInt(current.consumedEnergy) }} consumed
            @if (current.allocatedEnergy != null) {
              / {{ formatInt(current.allocatedEnergy) }} allocated
            } @else {
              &middot; no allocation configured
            }
          </div>
          <div class="progress-bar">
            <span
              class="progress-fill"
              [ngClass]="statusClass(current.energyStatus)"
              [style.width.%]="progressWidth(current.energyUtilizationPercentage)"
            ></span>
          </div>
        </div>

        <div class="indicator-card" data-testid="bandwidth-indicator">
          <div class="indicator-header">
            <span class="indicator-title">Bandwidth utilization today</span>
            <span class="status-pill" [ngClass]="statusClass(current.bandwidthStatus)">{{
              statusLabel(current.bandwidthStatus)
            }}</span>
          </div>
          <div class="indicator-percentage">{{ formatPercent(current.bandwidthUtilizationPercentage) }}</div>
          <div class="indicator-figures">
            {{ formatInt(current.consumedBandwidth) }} consumed
            @if (current.allocatedBandwidth != null) {
              / {{ formatInt(current.allocatedBandwidth) }} allocated
            } @else {
              &middot; no allocation configured
            }
          </div>
          <div class="progress-bar">
            <span
              class="progress-fill"
              [ngClass]="statusClass(current.bandwidthStatus)"
              [style.width.%]="progressWidth(current.bandwidthUtilizationPercentage)"
            ></span>
          </div>
        </div>
      </div>

      <div class="reserve-section">
        <h3 class="section-title">Reserve consumption today</h3>

        @if (hasReserveConsumption) {
          <div class="reserve-list">
            @for (item of current.reserveConsumptionByCategory; track item.category) {
              <div class="reserve-row" [attr.data-testid]="'reserve-category-' + item.category">
                <span class="reserve-category">{{ categoryLabel(item.category) }}</span>
                <span class="reserve-figures">
                  <span>Energy: {{ formatInt(item.energy) }}</span>
                  <span>Bandwidth: {{ formatInt(item.bandwidth) }}</span>
                </span>
              </div>
            }
          </div>
        } @else {
          <p class="reserve-empty" data-testid="reserve-empty">The Reserve has not been drawn on today.</p>
        }
      </div>
    }
  </section>

  <section class="section-card report-card">
    <div class="report-header">
      <h2 class="card-title">Report</h2>

      <div class="filters-row">
        <app-date-picker
          [selectedPeriod]="selectedPeriod"
          (presetSelected)="onPresetSelected($event)"
          (rangeChange)="onRangeSelected($event)"
        ></app-date-picker>

        <ng-container *appHasPermission="{ resource: Resources.Reports, scope: Scopes.Export }">
          <app-button
            buttonVariant="accent"
            buttonType="button"
            testId="export-utilization-report"
            [state]="isExporting ? 'loading' : 'default'"
            (buttonClick)="onExport()"
          >
            <img src="shared-assets/icons/download.png" alt="Export Icon" class="icon" />
            <span>Export</span>
          </app-button>
        </ng-container>
      </div>
    </div>

    <div class="table-wrapper">
      @if (isLoadingReport) {
        <app-loading-spinner size="large" [isOverlay]="true"></app-loading-spinner>
      } @else if (reportFailed) {
        <app-empty-state
          icon="error_outline"
          title="Failed to load the utilization report"
          subtitle="Check that a TRON network is configured, then try again."
        ></app-empty-state>
      } @else if (!hasReportRows) {
        <app-empty-state
          title="No utilization data for the selected range."
          subtitle="Try a wider date range."
        ></app-empty-state>
      } @else {
        <div class="report-table-scroll">
          <table class="report-table" data-testid="utilization-report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Staked energy</th>
                <th>Consumed energy</th>
                <th>Allocated energy</th>
                <th>Energy %</th>
                <th>Energy status</th>
                <th>Staked bandwidth</th>
                <th>Consumed bandwidth</th>
                <th>Allocated bandwidth</th>
                <th>Bandwidth %</th>
                <th>Bandwidth status</th>
              </tr>
            </thead>
            <tbody>
              @for (row of pageRows; track row.date) {
                <tr [attr.data-testid]="'report-row-' + row.date">
                  <td>{{ row.date }}</td>
                  <td>{{ formatInt(row.totalStakedEnergy) }}</td>
                  <td>{{ formatInt(row.consumedEnergy) }}</td>
                  <td>{{ formatInt(row.allocatedEnergy) }}</td>
                  <td>{{ formatPercent(row.energyUtilizationPercentage) }}</td>
                  <td>
                    <span class="status-pill" [ngClass]="statusClass(row.energyStatus)">{{
                      statusLabel(row.energyStatus)
                    }}</span>
                  </td>
                  <td>{{ formatInt(row.totalStakedBandwidth) }}</td>
                  <td>{{ formatInt(row.consumedBandwidth) }}</td>
                  <td>{{ formatInt(row.allocatedBandwidth) }}</td>
                  <td>{{ formatPercent(row.bandwidthUtilizationPercentage) }}</td>
                  <td>
                    <span class="status-pill" [ngClass]="statusClass(row.bandwidthStatus)">{{
                      statusLabel(row.bandwidthStatus)
                    }}</span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (pageCount > 1) {
          <div class="pager" data-testid="report-pager">
            <span class="pager-range" data-testid="pager-range">{{ pageRangeLabel }}</span>
            <button
              type="button"
              class="pager-button"
              aria-label="Previous page"
              data-testid="pager-previous"
              [disabled]="page === 1"
              (click)="previousPage()"
            >
              <mat-icon>chevron_left</mat-icon>
            </button>
            <span class="pager-page">Page {{ page }} of {{ pageCount }}</span>
            <button
              type="button"
              class="pager-button"
              aria-label="Next page"
              data-testid="pager-next"
              [disabled]="page === pageCount"
              (click)="nextPage()"
            >
              <mat-icon>chevron_right</mat-icon>
            </button>
          </div>
        }
      }
    </div>
  </section>
</div>
